// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import moment from "moment";
import { Button, Input, Tooltip, Modal, Table, Flex, Typography, Form, Dropdown as AntDropdown, Row, Col } from "antd";
import { CaretDownFilled, SearchOutlined, PlusOutlined, EditOutlined, DownOutlined } from "@ant-design/icons";

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { secondsToTime } from "helpers/generic";
import Categories from "components/UI/Categories";
import VideoPreview from "components/UI/Video/VideoPreview";
import VideoPlayerModal from "components/UI/VideoPlayer/VideoPlayerModal";
import { LiveStreamIcon, ScheduledLiveStreamIcon, ScheduledOnDemandIcon, OnDemandIcon } from "components/Icons";
import Dropdown from "components/Dropdown";

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import {
	selectMembersWorkoutsSelectedCategories,
	selectMembersWorkoutsSelectedDuration,
	selectMembersWorkoutsSelectedMusic,
	selectMembersWorkoutsSelectedEquipment,
	selectMembersWorkoutsSelectedTrainers,
} from "store/selectors";
import {
	updateMembersWorkoutsSelectedCategories,
	updateSelectedWorkout,
	updateMembersWorkoutsSelectedEquipment,
	updateMembersWorkoutsSelectedTrainers,
	updateMembersWorkoutsSelectedMusic,
	updateMembersWorkoutsSelectedDuration,
} from "store/slices";
import {
	useEditWorkoutMutation,
	useGetCategoriesQuery,
	useGetDomainGymQuery,
	useGetGenresQuery,
	useGetGymWorkoutsQuery,
	useGetScheduledStreamsQuery,
	useGetCoachesQuery,
	useGetGymEquipmentQuery,
} from "store/api/api";
import { FormSelect } from "components/Common/Form/FormSelect";
import { CreateWorkoutModal } from "components/Modals/CreateWorkoutModal";
import { ScheduleLiveModal, PreRecordedLive } from "components/Modals/ScheduleLiveModal";

// -----------------------------------------------------------------------------
// Style, utils and assets
// -----------------------------------------------------------------------------

import { defaultTimezone, workoutTypes, workoutStatuses } from "helpers/constants";
import "./styles.scss";
import dayjs from "dayjs";
import { sortByProperty } from "utils";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const WorkoutActiveToggle = ({ workout, scheduledStreams, handleChangeStatus }) => {
	const stream = scheduledStreams?.find((stream) => workout.id === stream.workout_id);
	const timezone = workout?.time_zone || stream?.time_zone || defaultTimezone;
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const getWorkoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });

	const isScheduled =
		workout.type === workoutTypes.SCHEDULED_ON_DEMAND &&
		workout.start_time &&
		dayjs(workout.start_time).isAfter(dayjs());
	const isScheduledPreRecorded = workout.type === workoutTypes.SCHEDULED_PRE_RECORDED;
	const isWorkoutStatusUpdateDisabled =
		isScheduled ||
		isScheduledPreRecorded ||
		workout.type === workoutTypes.CURRENTLY_LIVE ||
		(workout.status === workoutStatuses.DRAFT &&
			![workoutTypes.SCHEDULED_LIVE_STREAM, workoutTypes.SCHEDULED_ON_DEMAND].includes(workout.type) &&
			(!workout.name || !workout.description || !workout.image_url || !workout.coach || !workout.playback_id));

	useEffect(() => {
		if (workout.start_time) {
			const timeDifferenceMs = moment
				.tz(workout.start_time, "YYYY-MM-DDTHH:mm:ss", timezone)
				.diff(moment().tz(timezone));
			const timeout = setTimeout(() => {
				getWorkoutsResponse.refetch();
			}, timeDifferenceMs + 1);
			return () => clearTimeout(timeout);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timezone, workout.start_time]);

	return (
		<Flex className={`workout-active-toggle ${workout.status}`} align="center" justify="center">
			<FormSelect
				value={workout.status}
				onChange={(value) => handleChangeStatus(value, {}, workout)}
				style={{
					width: 114,
					padding: "0 8px",
				}}
				size="small"
				options={[
					{
						label: "Draft",
						value: workoutStatuses.DRAFT,
						disabled: [
							workoutTypes.CURRENTLY_LIVE,
							workoutTypes.SCHEDULED_LIVE_STREAM,
							workoutTypes.SCHEDULED_PRE_RECORDED,
						].includes(workout.type),
					},
					{ label: "Inactive", value: workoutStatuses.INACTIVE, disabled: isWorkoutStatusUpdateDisabled },
					{
						label: "Active",
						value: workoutStatuses.ACTIVE,
						disabled:
							isWorkoutStatusUpdateDisabled ||
							!workout.playback_id ||
							[workoutTypes.SCHEDULED_ON_DEMAND].includes(workout.type),
					},
				]}
				data-id="selectWorkoutState"
			/>
		</Flex>
	);
};

const SearchFilters = ({
	categoriesMenu,
	selectedCategories,
	trainersMenu,
	equipmentMenu,
	trainerFilters,
	equipmentFilters,
	genreMenu,
	selectedGenres,
	durationMenu,
	selectedDurations,
}) => {
	return (
		<div className="sb-workouts-table__search-filters" data-id="sectionWorkoutsSearchFilters">
			<Row style={{ gridRowGap: 6 }} gutter={{ xs: 16, md: 8 }}>
				<Col xl={5} lg={8} xs={24} data-id="txtWorkoutsCategoriesFilter">
					<Dropdown trigger={["click"]} menu={categoriesMenu} overlayClassName="dropdown-no-close">
						<Button
							style={{
								borderColor: " #E3E5E7",
								textTransform: "none",
								color: "#4F5661",
								fontWeight: "700",
								fontSize: "14px",
								lineHeight: "24px",
								fontFamily: "DM Sans",
								width: "100%",
								flex: 1,
							}}
						>
							Category
							{selectedCategories.length > 0 && selectedCategories[0] !== 0 && `(${selectedCategories.length})`}
							<CaretDownFilled
								style={{
									color: "#4F5661",
									fontSize: "12px",
									lineHeight: "0px",
								}}
							/>
						</Button>
					</Dropdown>
				</Col>
				<Col xl={5} lg={8} xs={12} data-id="txtWorkoutsTrainersFilter">
					<Dropdown trigger={["click"]} menu={trainersMenu} overlayClassName="dropdown-no-close">
						<Button
							style={{
								borderColor: " #E3E5E7",
								textTransform: "none",
								color: "#4F5661",
								fontWeight: "700",
								fontSize: "14px",
								lineHeight: "24px",
								fontFamily: "DM Sans",
								width: "100%",
							}}
						>
							Trainers {trainerFilters.length > 0 && `(${trainerFilters.length})`}
							<CaretDownFilled
								style={{
									color: "#4F5661",
									fontSize: "12px",
									lineHeight: "0px",
								}}
							/>
						</Button>
					</Dropdown>
				</Col>
				<Col xl={5} lg={8} xs={12} data-id="txtWorkoutsEquipmentFilter">
					<Dropdown trigger={["click"]} menu={equipmentMenu} overlayClassName="dropdown-no-close">
						<Button
							style={{
								borderColor: " #E3E5E7",
								textTransform: "none",
								color: "#4F5661",
								fontWeight: "700",
								fontSize: "14px",
								lineHeight: "24px",
								fontFamily: "DM Sans",
								width: "100%",
							}}
						>
							Equipment {equipmentFilters.length > 0 && `(${equipmentFilters.length})`}
							<CaretDownFilled
								style={{
									color: "#4F5661",
									fontSize: "12px",
									lineHeight: "0px",
								}}
							/>
						</Button>
					</Dropdown>
				</Col>
				<Col xl={5} lg={8} xs={12} data-id="txtWorkoutsDurationFilter">
					<Dropdown trigger={["click"]} menu={durationMenu} overlayClassName="dropdown-no-close">
						<Button
							style={{
								borderColor: " #E3E5E7",
								textTransform: "none",
								color: "#4F5661",
								fontWeight: "700",
								fontSize: "14px",
								lineHeight: "24px",
								fontFamily: "DM Sans",
								width: "100%",
							}}
						>
							Duration {selectedDurations.length > 0 && `(${selectedDurations.length})`}
							<CaretDownFilled
								style={{
									color: "#4F5661",
									fontSize: "12px",
									lineHeight: "0px",
								}}
							/>
						</Button>
					</Dropdown>
				</Col>
				<Col xl={4} lg={8} xs={12} data-id="txtWorkoutsGenreFilter">
					<Dropdown trigger={["click"]} menu={genreMenu} overlayClassName="dropdown-no-close">
						<Button
							style={{
								borderColor: " #E3E5E7",
								textTransform: "none",
								color: "#4F5661",
								fontWeight: "700",
								fontSize: "14px",
								lineHeight: "24px",
								fontFamily: "DM Sans",
								width: "100%",
							}}
						>
							Music {selectedGenres.length > 0 && `(${selectedGenres.length})`}
							<CaretDownFilled
								style={{
									color: "#4F5661",
									fontSize: "12px",
									lineHeight: "0px",
								}}
							/>
						</Button>
					</Dropdown>
				</Col>
			</Row>
		</div>
	);
};
const WorkoutsTable = ({
	setShowEditWorkoutModal,
	setSelectedLiveStream,
	setIsEditingLive,
	updateCount,
	workoutType,
	hideBulkStatusChange,
}) => {
	const dispatch = useDispatch();
	let { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;
	const { data: domainGym } = domainGymResponse;
	const primaryColor = domainGym?.brand_color1 || "#ff865c";

	const workoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const [workouts, setWorkouts] = useState([]);
	const categoriesResponse = useGetCategoriesQuery(domainGymId, { skip: !domainGymResponse.data });
	const categories = categoriesResponse.data?.categories || [];
	const genresResponse = useGetGenresQuery();
	const genres = genresResponse.data?.genres || [];
	const scheduledLiveStreamWorkouts =
		workoutsResponse.data?.workouts?.filter((workout) => workout.type === workoutTypes.SCHEDULED_LIVE_STREAM) || [];
	const { data: scheduledStreams } = useGetScheduledStreamsQuery(domainGymId, { skip: !domainGymResponse.data });
	const selectedCategories = useSelector(selectMembersWorkoutsSelectedCategories);

	const [editWorkoutTrigger] = useEditWorkoutMutation();
	const [isShowingVideoPlayer, updateIsShowingVideoPlayer] = useState(false);
	const [fileSelected, updateFileSelected] = useState(null);
	const [searchText, setSearchText] = useState("");
	const categoriesFilters = useSelector(selectMembersWorkoutsSelectedCategories);
	const equipmentFilters = useSelector(selectMembersWorkoutsSelectedEquipment);
	const trainerFilters = useSelector(selectMembersWorkoutsSelectedTrainers);
	const selectedGenres = useSelector(selectMembersWorkoutsSelectedMusic);
	const selectedDurations = useSelector(selectMembersWorkoutsSelectedDuration);
	const { data: coachesData } = useGetCoachesQuery(domainGymId, {
		skip: !domainGymId,
	});
	const { data: equipments } = useGetGymEquipmentQuery(domainGymId, {
		skip: !domainGymId,
	});
	const setEquipmentFilters = (filters) => {
		dispatch(updateMembersWorkoutsSelectedEquipment(filters));
	};

	const setTrainerFilters = (filters) => {
		dispatch(updateMembersWorkoutsSelectedTrainers(filters));
	};

	const setSelectedGenres = (filters) => {
		dispatch(updateMembersWorkoutsSelectedMusic(filters));
	};
	const setDurationFilters = (filters) => {
		dispatch(updateMembersWorkoutsSelectedDuration(filters));
	};

	const [showLiveScheduleModal, setShowLiveScheduleModal] = useState(false);
	const [showCreateWorkoutModal, setShowCreateWorkoutModal] = useState(false);
	const [showPreRecordedLiveModal, setShowPreRecordedLiveModal] = useState(false);

	const setWorkoutSelected = useCallback(
		(workout) => {
			dispatch(updateSelectedWorkout({ workout }));
			setShowEditWorkoutModal(true);
		},
		[dispatch, setShowEditWorkoutModal]
	);
	const [toggleDisabled, setToggleDisabled] = useState(false);

	const handleChangeSearchText = (e) => {
		setSearchText(e?.target?.value);
	};

	useEffect(() => {
		if (workoutsResponse.data) {
			setWorkouts(workoutsResponse.data?.workouts || []);
		}
	}, [workoutsResponse]);

	const handleCategoryClick = (category) => {
		const category_id = parseInt(category.key);

		if (!selectedCategories.includes(category_id)) {
			if (category_id === 0) {
				dispatch(updateMembersWorkoutsSelectedCategories([0]));
			} else {
				if (selectedCategories.includes(0)) {
					dispatch(updateMembersWorkoutsSelectedCategories([category_id]));
				} else {
					dispatch(updateMembersWorkoutsSelectedCategories([...selectedCategories, category_id]));
				}
			}
		} else if (category_id !== 0) {
			const newCategories = [...selectedCategories];

			newCategories.splice(
				selectedCategories.findIndex((c) => c === category_id),
				1
			);

			dispatch(updateMembersWorkoutsSelectedCategories(newCategories.length > 0 ? newCategories : [0]));
		}
	};

	const [modal, contextHolder] = Modal.useModal();
	const handleEditWorkoutActive = async (workout, status) => {
		try {
			const payload = {
				workout_id: workout.id,
				gym_id: workout.gym_id,
				status,
			};
			const isScheduledWorkout = workout.type === workoutTypes.SCHEDULED_ON_DEMAND;
			const isDraft = status === workoutStatuses.DRAFT;
			if (isDraft && isScheduledWorkout) {
				modal.confirm({
					content: "Changing the workout to Draft will cancel the workout release schedule.",
					okText: "Confirm",
					cancelText: "Go Back",
					icon: null,
					maskClosable: false,
					title: "Confirm",
					width: 520,
					onCancel: () => form.setFieldValue("workoutStatus", workout.status),
					onOk: async () => {
						await editWorkoutTrigger({
							...payload,
							start_time: null,
							is_scheduled: 0,
							time_zone: null,
							type: workoutTypes.ON_DEMAND,
						}).unwrap();
						workoutsResponse.refetch();
					},
				});

				return;
			}

			await editWorkoutTrigger(payload).unwrap();
			workoutsResponse.refetch();
		} catch (error) {
			console.error("handleEditWorkoutActive error: ", error);
		}
	};

	const handleChangeStatus = async (checked, event, record) => {
		await handleEditWorkoutActive(record, checked);
	};

	//Why is this showing double
	const categoryItems = categories
		.map((category) => {
			return {
				label: <div className="dropdown-list-item">{category.name?.trim?.() || "Empty Category"}</div>,
				key: category.id,
				danger: categoriesFilters.includes(category.id) ? true : false,
				item: category.name,
				title: category.name,
			};
		})
		.sort((optionA, optionB) => (optionA?.item ?? "").toLowerCase().localeCompare((optionB?.item ?? "").toLowerCase()));

	const categoriesMenu = {
		items: [
			{
				label: "All Categories",
				key: 0,
				danger: categoriesFilters.includes(0) ? true : false,
				title: "All Categories",
			},
			...sortByProperty(categoryItems, "title"),
		],
		onClick: handleCategoryClick,
	};
	const allTrainers = coachesData?.coaches || [];

	const trainerItems = allTrainers
		.map((item, index) => {
			return {
				label: <div className="dropdown-list-item">{item?.name?.trim?.() || "No Trainer"}</div>,
				key: item.id,
				danger: !!trainerFilters.includes(item.id),
				item,
				onClick: () => handleTrainerClick(item?.id?.trim?.()),
				title: item.name?.trim?.() || "No Trainer",
			};
		})
		.sort((optionA, optionB) =>
			(optionA?.item?.name ?? "").toLowerCase().localeCompare((optionB?.item?.name ?? "").toLowerCase())
		);

	const handleTrainerClick = (clickedTrainer) => {
		if (trainerFilters.includes(clickedTrainer)) {
			const newTrainers = trainerFilters.filter((g) => g !== clickedTrainer);
			setTrainerFilters(newTrainers);
		} else {
			setTrainerFilters([...trainerFilters, clickedTrainer]);
		}
	};

	const trainersMenu = {
		items: [
			{
				label: "All",
				key: "all",
				onClick: () => {
					setTrainerFilters([]);
				},
				danger: !trainerFilters.length,
			},
			...trainerItems,
		],
	};

	const allEquipment = equipments?.equipments?.map((equipment) => equipment.name?.trim?.()) || [];
	const uniqueEquipment = [...new Set(allEquipment)];

	const handleEquipmentClick = (clickedEquipment) => {
		if (equipmentFilters.includes(clickedEquipment)) {
			const newEquipment = equipmentFilters.filter((g) => g !== clickedEquipment);
			setEquipmentFilters(newEquipment);
		} else {
			setEquipmentFilters([...equipmentFilters, clickedEquipment]);
		}
	};

	const equipmentItems = uniqueEquipment
		.map((item, index) => {
			return {
				label: <div className="dropdown-list-item">{item?.trim?.() || "No Equipment"}</div>,
				value: item,
				key: index,
				danger: equipmentFilters.includes(item) ? true : false,
				item,
				onClick: () => handleEquipmentClick(item),
			};
		})
		.sort((optionA, optionB) => (optionA?.item ?? "").toLowerCase().localeCompare((optionB?.item ?? "").toLowerCase()));

	const equipmentMenu = {
		items: [
			{
				label: "All",
				key: "all",
				danger: !equipmentFilters.length,
				onClick: () => {
					setEquipmentFilters([]);
				},
			},
			...equipmentItems,
		],
	};

	const handleGenreClick = (clicked) => {
		const clickedGenre = genres[clicked.key];
		const isAlreadySelected = selectedGenres.map((g) => g.title).includes(clickedGenre.title);
		if (isAlreadySelected) {
			const newGenres = selectedGenres.filter((g) => g.title !== clickedGenre.title);
			setSelectedGenres(newGenres);
		} else {
			setSelectedGenres([...selectedGenres, clickedGenre]);
		}
	};

	const genreItems = genres.map((item, index) => {
		return {
			label: <div className="dropdown-list-item">{item.title?.trim?.() || "No Genre"}</div>,
			key: index,
			danger: selectedGenres.map((g) => g.title).includes(genres[index].title) ? true : false,
			item: item.title,
			onClick: () => handleGenreClick({ key: index }),
		};
	});

	const genreMenu = {
		items: [
			{
				label: "All",
				key: "all",
				danger: !selectedGenres.length,
				onClick: () => {
					setSelectedGenres([]);
				},
			},
			...genreItems.sort((optionA, optionB) =>
				(optionA?.item ?? "").toLowerCase().localeCompare((optionB?.item ?? "").toLowerCase())
			),
		],
	};

	const durations = [
		{ min: 0, max: 15, label: "0-15 min" },
		{ min: 15, max: 30, label: "15-30 min" },
		{ min: 30, max: 45, label: "30-45 min" },
		{ min: 45, max: 60, label: "45-60 min" },
		{ min: 60, max: 90, label: "60-90 min" },
		{ min: 90, max: 120, label: "90-120 min" },
		{ min: 120, max: 1000, label: "120+ min" },
	];

	const handleDurationClick = (duration) => {
		const clicked = durations[duration.key];
		const selectedLabels = selectedDurations.map((d) => d.label);
		const isAlreadySelected = selectedLabels.includes(clicked.label);
		if (isAlreadySelected) {
			const newDurations = selectedDurations.filter((d) => d.label !== clicked.label);
			setDurationFilters(newDurations);
		} else {
			setDurationFilters([...selectedDurations, clicked]);
		}
	};

	const [changeStatus, setChangeStatus] = useState(false);
	const [form] = Form.useForm();

	const isWithinDuration = (workout) => {
		const duration = workout.duration / 60;
		const selectedDurationsLabels = new Set(selectedDurations.map((d) => d.label));

		if (selectedDurationsLabels.size === 0) return true;

		return durations.some(
			({ label, min, max }) => selectedDurationsLabels.has(label) && duration >= min && duration <= max
		);
	};

	const isNotScheduledLiveStreamWorkout = (workout) => {
		const stream = scheduledStreams?.find((stream) => stream.workout_id === workout.id);
		const scheduledLiveStreamWorkout = scheduledLiveStreamWorkouts?.find((w) => w.id === workout.id);

		return (!scheduledLiveStreamWorkout && !stream) || workout.is_previously_live;
	};

	const workoutsToActivate = workouts
		.filter((workout) => isNotScheduledLiveStreamWorkout(workout))
		.filter((workout) => isWithinDuration(workout))
		.filter(
			(workout) =>
				categoriesFilters.includes(0) || workout.categories.some((c) => categoriesFilters.includes(c.category_id))
		)
		.filter((workout) => {
			const coaches = workout?.coaches?.map(({ id }) => id) || [];

			if (!trainerFilters.length) {
				return true;
			}
			return trainerFilters.some((coach) => coaches.includes(coach));
		})
		.filter((workout) =>
			equipmentFilters.length > 0
				? workout.equipment.split(",").some((e) => equipmentFilters.includes(e?.trim()))
				: true
		)
		.filter(
			(workout) => selectedGenres.map((g) => g.title).includes(workout.music_genre) || selectedGenres.length === 0
		)
		.filter((workout) => workout.duration !== 0)
		.filter(
			(workout) =>
				workout.name?.toLowerCase().includes(searchText.toLowerCase()) ||
				workout.description?.toLowerCase().includes(searchText.toLowerCase())
		)
		.filter((workout) => {
			if (workoutType) {
				return workoutType.includes(workout.type);
			}
			return true;
		})
		.filter((workout) => workout.type !== workoutTypes.SCHEDULED_ON_DEMAND);
	const bulkChangeStatus = async (status) => {
		setToggleDisabled(true);
		const filteredWorkouts = workoutsToActivate
			.filter((item) => {
				const valid = item.status !== status;
				if (item.status === workoutStatuses.DRAFT) {
					return (
						valid &&
						item.name &&
						item.description &&
						item.image_url &&
						item.coach &&
						(item.video_url || item?.playback_id)
					);
				}
				return valid;
			})
			.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

		await Promise.all(filteredWorkouts.map((workout) => handleEditWorkoutActive(workout, status)));
		setToggleDisabled(false);
		workoutsResponse.refetch();
		setChangeStatus(false);
	};

	const durationItems = durations.map((item, index) => {
		return {
			label: item.label,
			key: index,
			danger: selectedDurations.map((d) => d.label).includes(durations[index].label) ? true : false,
			onClick: () => handleDurationClick({ key: index }),
		};
	});

	const durationMenu = {
		items: [
			{
				label: "All",
				key: "all",
				danger: !selectedDurations.length,
				onClick: () => {
					setDurationFilters([]);
				},
			},
			...durationItems,
		],
	};

	const getFormattedStartTime = (startTime, timezone) => {
		return moment.tz(startTime, "YYYY-MM-DDTHH:mm:ss", timezone).format("MM/DD h:mm A z");
	};

	const [vals, setVals] = useState({});
	const getWorkoutTypeComponent = (workout) => {
		const stream = scheduledStreams?.find((stream) => workout.id === stream.workout_id);
		const timezone = workout?.time_zone || stream?.time_zone || defaultTimezone;
		const isScheduledOnDemand = workout?.type === workoutTypes.SCHEDULED_ON_DEMAND;
		const isScheduledLiveStream =
			workout?.type === workoutTypes.SCHEDULED_LIVE_STREAM || workout?.type === workoutTypes.SCHEDULED_PRE_RECORDED;
		const isCurrentlyLive = workout?.type === workoutTypes.CURRENTLY_LIVE;
		const isOnDemand = workout?.type === workoutTypes.ON_DEMAND;

		const getWorkoutTypeItem = (title, IconComponent, iconProps, className) => (
			<Tooltip title={title}>
				<div className={`workout-type-box ${className}`}>
					<IconComponent {...iconProps} fill={isCurrentlyLive ? "#ffffff" : primaryColor} />
				</div>
			</Tooltip>
		);

		if (isCurrentlyLive) {
			return getWorkoutTypeItem(
				"Livestreaming",
				LiveStreamIcon,
				{ fill: "white", width: 20, height: 19 },
				"workout-type-box__live"
			);
		}

		if (isScheduledLiveStream) {
			const startTimeFormatted = getFormattedStartTime(workout?.start_time, timezone);
			return getWorkoutTypeItem(
				`Livestreaming at ${startTimeFormatted}`,
				ScheduledLiveStreamIcon,
				{ fill: primaryColor, width: 20, height: 19 },
				"workout-type-box__on-demand"
			);
		}

		if (isScheduledOnDemand) {
			const startTimeFormatted = getFormattedStartTime(workout?.start_time, timezone);
			return getWorkoutTypeItem(
				`The workout will activate at ${startTimeFormatted}`,
				ScheduledOnDemandIcon,
				{ fill: primaryColor, width: 20, height: 19 },
				"workout-type-box__on-demand"
			);
		}

		if (isOnDemand) {
			return getWorkoutTypeItem(
				"On demand workout",
				OnDemandIcon,
				{ fill: primaryColor, width: 12, height: 12 },
				"workout-type-box__on-demand"
			);
		}

		// Default On Demand
		return getWorkoutTypeItem(
			workout.type?.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) || "On demand",
			OnDemandIcon,
			{ fill: primaryColor, width: 12, height: 12 },
			"workout-type-box__on-demand"
		);
	};

	const columns = [
		{
			title: "Video",
			dataIndex: "video",
			key: "video",
			render: (_, record) => {
				return (
					<div className="video-column-container" data-id="clVideo">
						<div className="workout-type" data-id="workoutTypeIcon">
							{getWorkoutTypeComponent(record)}
						</div>
						<VideoPreview
							updateIsShowingVideoPlayer={updateIsShowingVideoPlayer}
							updateFileSelected={updateFileSelected}
							record={record}
						/>
					</div>
				);
			},
		},
		{
			title: "Status ",
			dataIndex: "status",
			key: "status",
			align: "left",
			filtered: false,
			filterDropdown: <></>,
			filterDropdownOpen: false,
			filterIcon: <></>,
			render: (_, record) => (
				<WorkoutActiveToggle
					workout={record}
					handleChangeStatus={handleChangeStatus}
					scheduledStreams={scheduledStreams}
				/>
			),
		},
		{
			title: "Music",
			dataIndex: "music",
			key: "music",
			render: (_, record) => {
				return <span>{record.music_genre}</span>;
			},
		},
		{
			title: "Categories",
			dataIndex: "categories",
			key: "categories",
			className: "categories-column",
			render: (row) => (row ? <Categories values={row.map((r) => r.name)} /> : null),
		},
		{
			title: "Duration",
			dataIndex: "duration",
			key: "duration",
			render: (_, record) => {
				return record.type === workoutTypes.CURRENTLY_LIVE ? (
					<span>Processing...</span>
				) : (
					<span>{secondsToTime(record.duration)}</span>
				);
			},
		},
		{
			title: "Actions",
			dataIndex: "actions",
			key: "actions",
			fixed: "right",
			width: 100,
			render: (_, record) => {
				return (
					<div
						className={
							record.type === workoutTypes.CURRENTLY_LIVE
								? "sb-workouts-table__edit-disabled"
								: "sb-workouts-table__edit"
						}
						data-id="bntWorkoutEdit"
						onClick={() => {
							if (record.type === workoutTypes.CURRENTLY_LIVE) return;

							const stream = scheduledStreams?.find((stream) => record.id === stream.workout_id);
							const scheduledLiveStreamWorkout = scheduledLiveStreamWorkouts?.find((w) => w.id === record.id);

							setWorkoutSelected(scheduledLiveStreamWorkout ?? record);
							setSelectedLiveStream(stream);
							setIsEditingLive(!!scheduledLiveStreamWorkout);
						}}
						style={{ color: primaryColor }}
					>
						<Flex gap={8} justify="center" align="center">
							<EditOutlined /> <span style={{ color: primaryColor }}>Edit</span>
						</Flex>
					</div>
				);
			},
		},
	];

	const dataSource = workouts
		.filter((workout) => isWithinDuration(workout))
		.filter(
			(workout) =>
				categoriesFilters.includes(0) || workout.categories.some((c) => categoriesFilters.includes(c.category_id))
		)
		.filter((workout) => {
			const coaches = workout?.coaches?.map(({ id }) => id) || [];
			if (!trainerFilters.length) {
				return true;
			}
			return trainerFilters.some((coach) => coaches.includes(coach));
		})
		.filter((workout) =>
			equipmentFilters.length > 0
				? workout.equipment.split(",").some((e) => equipmentFilters.includes(e?.trim())) ||
				  workout?.equipment_list?.map((e) => e.name).some((e) => equipmentFilters.includes(e?.trim()))
				: true
		)
		.filter(
			(workout) => selectedGenres.map((g) => g.title).includes(workout.music_genre) || selectedGenres.length === 0
		)
		.filter(
			(workout) =>
				workout.name?.toLowerCase().includes(searchText.toLowerCase()) ||
				workout.description?.toLowerCase().includes(searchText.toLowerCase())
		)
		.filter((workout) => {
			if (workoutType) {
				return workoutType.includes(workout.type);
			}
			return (
				workout?.type === workoutTypes.SCHEDULED_ON_DEMAND ||
				workout?.type === workoutTypes.ON_DEMAND ||
				!workout.start_time
			);
		})
		.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

	useEffect(() => {
		if (updateCount) {
			updateCount(dataSource.length);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataSource.length]);
	return (
		<div className="sb-workouts-table">
			<SearchFilters
				handleChangeSearchText={handleChangeSearchText}
				categoriesMenu={categoriesMenu}
				selectedCategories={selectedCategories}
				trainersMenu={trainersMenu}
				equipmentMenu={equipmentMenu}
				trainerFilters={trainerFilters}
				equipmentFilters={equipmentFilters}
				genreMenu={genreMenu}
				selectedGenres={selectedGenres}
				durationMenu={durationMenu}
				selectedDurations={selectedDurations}
			/>
			<Flex gap={16} justify="space-between" className="filter-section">
				<Flex gap={16} align="center">
					<Input
						placeholder="Search Workouts"
						prefix={<SearchOutlined style={{ color: "grey", paddingRight: "8px" }} />}
						onChange={handleChangeSearchText}
						style={{ borderRadius: "100px", minWidth: "200px", flex: 1, height: 40 }}
						allowClear
						data-id="txtWorkoutsSearch"
					/>

					{!hideBulkStatusChange && (
						<Tooltip
							title={workoutsToActivate.length ? "Apply filters before batch activating" : "0 workouts selected"}
							trigger="hover"
						>
							<Button
								disabled={toggleDisabled}
								onClick={() => workoutsToActivate.length && setChangeStatus(true)}
								className="primary-color"
								size="small"
							>
								<EditOutlined />
								Change Status
							</Button>
						</Tooltip>
					)}
				</Flex>

				<Flex gap={16} align="center" justify="flex-end" className="workout-actions">
					<AntDropdown
						icon={<DownOutlined />}
						style={{ flex: 1 }}
						trigger={["click"]}
						menu={{
							items: [
								{
									label: "Schedule Live",
									key: "createLiveWorkout",
									onClick: () => {
										setShowLiveScheduleModal(true);
									},
								},
								{
									label: "Pre-recorded Live",
									key: "createOnDemandWorkout",
									onClick: () => {
										setShowPreRecordedLiveModal(true);
									},
								},
								{
									label: "New Workout",
									key: "createNewWorkout",
									onClick: () => {
										setShowCreateWorkoutModal(true);
									},
								},
							],
						}}
					>
						<Button type="primary" data-id="btnNew">
							<PlusOutlined /> New
							<DownOutlined />
						</Button>
					</AntDropdown>
				</Flex>
			</Flex>
			<ScheduleLiveModal open={showLiveScheduleModal} setOpen={setShowLiveScheduleModal} />
			<CreateWorkoutModal open={showCreateWorkoutModal} setOpen={setShowCreateWorkoutModal} />
			<PreRecordedLive open={showPreRecordedLiveModal} setOpen={setShowPreRecordedLiveModal} />
			<Modal
				title={<Typography.Title level={4}>Change Status</Typography.Title>}
				open={changeStatus}
				onCancel={() => setChangeStatus(false)}
				okText="Confirm"
				cancelText="Cancel"
				maskClosable={false}
				destroyOnClose
				footer={
					<div style={{ marginTop: 25 }} className="sb-confirm-form__actions">
						<Button size="small" htmlType="button" className="primary-color" onClick={() => setChangeStatus(false)}>
							Cancel
						</Button>
						<Button
							disabled={!vals.status}
							onClick={() => bulkChangeStatus(form.getFieldsValue()?.status)}
							size="small"
							type="primary"
						>
							Update
						</Button>
					</div>
				}
			>
				<Form onValuesChange={setVals} className={`workout-active-toggle`} form={form} layout="vertical">
					<FormSelect
						style={{
							width: "100%",
							marginBottom: 16,
						}}
						label={
							<Typography.Text style={{ textTransform: "none", fontWeight: 400 }}>Change Status to</Typography.Text>
						}
						name="status"
						placeholder={`Change Status to`}
						help={`${workoutsToActivate.length} workouts selected.`}
						options={[
							{ label: "Draft", value: workoutStatuses.DRAFT },
							{ label: "Inactive", value: workoutStatuses.INACTIVE },
							{ label: "Active", value: workoutStatuses.ACTIVE },
						]}
					/>
				</Form>
			</Modal>
			{workouts && (
				<Table
					scroll={{ x: dataSource?.length ? "max-content" : undefined }}
					data-id="tableWorkouts"
					columns={dataSource?.length ? columns : []}
					dataSource={dataSource}
					rowKey="id"
					style={{ marginTop: "16px" }}
					onRow={(record, index) => ({
						style: {
							background: record.type === workoutTypes.CURRENTLY_LIVE ? "rgba(0,0,0,.1)" : "default",
							cursor: record.type === workoutTypes.CURRENTLY_LIVE ? "null" : "pointer",
						},
					})}
				/>
			)}
			<VideoPlayerModal
				file={fileSelected}
				isShowing={isShowingVideoPlayer}
				updateIsShowingVideoPlayer={updateIsShowingVideoPlayer}
			/>
			{contextHolder}
		</div>
	);
};

export default WorkoutsTable;
